import {
    ALL_INSURANCE_PROVIDERS,
    ClientStatus,
    IClient,
    InsuranceType,
    RelationshipToPrimaryInsured,
    STATES,
} from "intake_shared/src/types";
import { useToasts } from "react-toast-notifications";
import { Input } from "../../../../bits/Input/Input";
import { Select } from "../../../../bits/Select/Select";
import { toTitleCase } from "../../../../../../utils/utils";
import styles from "./InsuranceForm.module.scss";
import {
    TUpdateClient,
    TUpdateMultipleClientFieldsByStatus,
} from "../../../../../../types";
import {
    buildSelfPayAutoFillToastMessage,
    getSelfPayInsurancePatch,
} from "../../../../../v2utils";

type Option = {
    label: React.ReactNode;
    value: string;
};

interface InsuranceFormProps {
    client: IClient;
    updateClient: TUpdateClient;
    updateMultipleClientFieldsByStatus: TUpdateMultipleClientFieldsByStatus;
    viewingInsuranceVerification?: boolean;
    viewingAppointmentSetting?: boolean;
}

const SELF_PAY_PROVIDER = "SELF-PAY";

const getClientStateFieldSelectOptions = () => {
    return STATES.map((state) => {
        return {
            label: state.name,
            value: state.abbrv,
        };
    });
};

const getInsuranceProviderOptions = ALL_INSURANCE_PROVIDERS.map((p) => ({
    label: p.toUpperCase(),
    value: p.toUpperCase(),
}));

const getInsuranceTypeOptions = Object.values(InsuranceType).map((p) => ({
    label: toTitleCase(p.replaceAll("_", " ")),
    value: p,
}));

const getRelationshipOptions = Object.values(RelationshipToPrimaryInsured).map(
    (p) => ({
        label: toTitleCase(p),
        value: p,
    })
);
const getAcceptAssignmentOptions = [
    {
        label: "Yes",
        value: "Y",
    },
    {
        label: "No",
        value: "N",
    },
];

export default function InsuranceForm({
    client,
    updateClient,
    updateMultipleClientFieldsByStatus,
    viewingInsuranceVerification,
    viewingAppointmentSetting = false,
}: InsuranceFormProps) {
    const { addToast } = useToasts();

    const handleProviderChange = (rawValue: string) => {
        const nextProvider = rawValue.toUpperCase();
        updateClient("insurance_provider", nextProvider);
        if (nextProvider !== SELF_PAY_PROVIDER) {
            return;
        }
        const patch = getSelfPayInsurancePatch(client);
        if (Object.keys(patch).length === 0) {
            return;
        }
        updateMultipleClientFieldsByStatus(ClientStatus.INS_VERIFICATION, patch);
        const message = buildSelfPayAutoFillToastMessage(patch);
        if (message) {
            addToast(message, { appearance: "success", autoDismiss: true });
        }
    };

    return (
        <div>
            {!viewingAppointmentSetting && (
                <div className="gridThree">
                    <Select
                        label="Insurance provider"
                        isSearchable
                        onChange={(val) =>
                            handleProviderChange((val as Option).value)
                        }
                        options={[
                            { label: "Select option", value: "" },
                            ...getInsuranceProviderOptions,
                        ]}
                        defaultValue={getInsuranceProviderOptions.find(
                            (item) =>
                                item.value ===
                                client.insurance_provider?.toUpperCase()
                        )}
                    />
                    <Input
                        label="Insurance member ID"
                        value={client.insurance_member_id || ""}
                        onChange={(e) => {
                            updateClient("insurance_member_id", e.target.value);
                        }}
                    />
                    <Select
                        label="Insurance type"
                        onChange={(val) =>
                            updateClient("insurance_type", (val as Option).value)
                        }
                        options={[
                            { label: "Select option", value: "" },
                            ...getInsuranceTypeOptions,
                        ]}
                        defaultValue={getInsuranceTypeOptions.find(
                            (item) => item.value === client.insurance_type
                        )}
                    />
                </div>
            )}
            <div className={styles.insuranceGrid}>
                {viewingInsuranceVerification && (
                    <Input
                        label="Address line"
                        value={client.address}
                        onChange={(e) => {
                            updateClient("address", e.target.value);
                        }}
                    />
                )}

                {viewingInsuranceVerification && (
                    <Select
                        id="state"
                        label="State"
                        isSearchable
                        onChange={(val) =>
                            updateClient("state", (val as Option).value)
                        }
                        options={[
                            { label: "Select option", value: "" },
                            ...getClientStateFieldSelectOptions(),
                        ]}
                        defaultValue={getClientStateFieldSelectOptions().find(
                            (state) => state.value === client.state
                        )}
                    />
                )}
                {viewingInsuranceVerification && (
                    <Input
                        label="City"
                        value={client.city}
                        onChange={(e) => {
                            updateClient("city", e.target.value);
                        }}
                    />
                )}

                <Select
                    label="Relationship to Primary Insured"
                    onChange={(val) =>
                        updateClient(
                            "relationship_to_primary_insured",
                            (val as Option).value
                        )
                    }
                    options={[
                        { label: "Select option", value: "" },
                        ...getRelationshipOptions,
                    ]}
                    defaultValue={getRelationshipOptions.find(
                        (item) =>
                            item.value === client.relationship_to_primary_insured
                    )}
                />
                {viewingInsuranceVerification ? (
                    <Input
                        label="Zip code"
                        value={client.zip_code}
                        onChange={(e) => {
                            updateClient("zip_code", e.target.value);
                        }}
                    />
                ) : (
                    <Select
                        options={[
                            { label: "Select option", value: "" },
                            ...getAcceptAssignmentOptions,
                        ]}
                        label="Accepts assignment"
                        onChange={(val) =>
                            updateClient("accept_assignment", (val as Option).value)
                        }
                        defaultValue={getAcceptAssignmentOptions.find(
                            (item) => item.value === client.accept_assignment
                        )}
                    />
                )}
                {viewingInsuranceVerification && (
                    <Select
                        options={[
                            { label: "Select option", value: "" },
                            ...getAcceptAssignmentOptions,
                        ]}
                        label="Accepts assignment"
                        onChange={(val) =>
                            updateClient("accept_assignment", (val as Option).value)
                        }
                        defaultValue={getAcceptAssignmentOptions.find(
                            (item) => item.value === client.accept_assignment
                        )}
                    />
                )}
            </div>
        </div>
    );
}
